import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import { type SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import type { MouseEvent } from "react";
import { useState } from "react";

import { MotifIcon } from "@/components/MotifIcon";
import { Tag } from "@/components/Tag";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { VibeCardData } from "@/pages/vibeLab/types";

const styles = {
  card: {
    width: "100%",
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
  },
  media: {
    height: 160,
    objectFit: "cover",
  },
  content: {
    padding: "16px",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  tags: {
    marginBottom: "12px",
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  description: {
    height: "44px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    color: theme.colors.neutral070,
  },
  updatedAt: {
    marginTop: "8px",
    color: theme.colors.neutral060,
  },
  actions: {
    display: "flex",
    borderTop: `1px solid ${theme.colors.neutral020}`,
  },
  actionButton: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "12px 0",
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
    "&:not(:last-child)": {
      borderRight: `1px solid ${theme.colors.neutral020}`,
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface VibeProjectCardProps {
  data: VibeCardData;
}

export const VibeProjectCard: React.FC<VibeProjectCardProps> =
  function VibeProjectCard({ data }) {
    const [liked, setLiked] = useState(false);

    const handleCardClick = useHandler(() => {
      if (data.externalUrl) {
        window.open(data.externalUrl, "_blank", "noopener,noreferrer");
      }
    });

    const handleLikeClick = useHandler((e: MouseEvent) => {
      e.stopPropagation();
      setLiked(!liked);
    });

    const isInternal = data.status === "Internal-use";
    const statusColor = data.status === "External-use" ? "success" : "primary";

    return (
      <Card sx={styles.card} onClick={handleCardClick}>
        <CardMedia
          component="img"
          alt={data.appName}
          image={data.coverImage || "https://placehold.co/600x300?text=AI+Lab"}
          sx={styles.media}
        />
        <CardContent sx={styles.content}>
          <Box sx={styles.tags}>
            <Tag label={data.status} color={statusColor} size="small" />
            {data.tags.map((tag) => (
              <Tag key={tag} label={tag} size="small" />
            ))}
          </Box>
          <Box>
            <Typography variant="h3">{data.appName}</Typography>
            <Box sx={styles.description}>
              <Typography variant="body">{data.description}</Typography>
            </Box>
            <Box sx={styles.updatedAt}>
              <Typography variant="note">Updated: {data.updatedAt}</Typography>
            </Box>
          </Box>
        </CardContent>
        <Box sx={styles.actions}>
          <Tooltip title={isInternal ? "Internal Only" : "Open Application"}>
            <Box sx={styles.actionButton}>
              {isInternal ? (
                <Box sx={{ color: theme.colors.warning }}>
                  <MotifIcon un-i-motif="lock" />
                </Box>
              ) : (
                <Box sx={{ color: theme.colors.primary }}>
                  <MotifIcon un-i-motif="earth" />
                </Box>
              )}
            </Box>
          </Tooltip>
          <Tooltip title={liked ? "已收到興趣" : "我有興趣"}>
            <Box sx={styles.actionButton} onClick={handleLikeClick}>
              {liked ? (
                <Box sx={{ color: theme.colors.error }}>
                  <MotifIcon un-i-motif="circle_check_filled" />
                </Box>
              ) : (
                <MotifIcon un-i-motif="circle_check" />
              )}
            </Box>
          </Tooltip>
        </Box>
      </Card>
    );
  };
